import { styled } from '@mui/material/styles';
import { Box, Paper, Typography } from '@mui/material';
import MuiAccordion from '@mui/material/Accordion';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import MuiAccordionSummary from '@mui/material/AccordionSummary';
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';

// ============================================
// Accordion
// ============================================
export const StyledAccordion = styled(MuiAccordion, {
  shouldForwardProp: prop => !['isExpanded'].includes(prop),
})(({ theme, isExpanded }) => ({
  background: '#222324',
  color: '#fff',
  disableGutters: true,
  elevation: 0,
  square: true,
  '& .MuiAccordionSummary-expandIconWrapper': {
    color: '#fff',
    padding: 0,
    margin: 0,
  },
  '& .MuiButtonBase-root.MuiAccordionSummary-root .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    color: '#0F7F85',
  },
  border: `1px solid ${theme.palette.divider}`,
  '&:not(:last-child)': {
    borderBottom: 0,
  },
  '&:before': {
    display: 'none',
  },
  '& .MuiButtonBase-root.MuiAccordionSummary-root': {
    padding: '0 16px 0 4px',
    width: '220px',
    height: '60px',
    '&:hover': {
      background: isExpanded ? 'black' : '#0F7F85',
    },
    background: isExpanded ? 'black' : 'inherit',
  },
}));

// ============================================
// Accordion Summary
// ============================================
const ExpandIcon = styled(ArrowForwardIosSharpIcon)({
  fontSize: '0.9rem',
});

export const StyledAccordionSummary = styled(props => <MuiAccordionSummary expandIcon={<ExpandIcon />} {...props} />)(
  ({ theme }) => ({
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, .05)' : 'rgba(0, 0, 0, .03)',
    flexDirection: 'reverse',
    '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
      transform: 'rotate(90deg)',
    },
    '& .MuiAccordionSummary-content': {
      marginLeft: theme.spacing(1),
    },
  }),
);

// ============================================
// Accordion Details
// ============================================
export const StyledAccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: '1px solid rgba(0, 0, 0, .125)',
  background: 'black',
}));

// ============================================
// Icon Wrapper
// ============================================
export const IconWrapper = styled(Box)({
  position: 'relative',
});

// 카카오톡/인스타 스타일 카운트 배지 (빨간 원 + 흰 숫자)
const countBadgeBase = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxSizing: 'border-box',
  minWidth: '16px',
  height: '16px',
  padding: '0 4px',
  borderRadius: '8px',
  background: '#FA5252',
  color: '#fff',
  fontSize: '10px',
  fontWeight: 700,
  lineHeight: 1,
};

// 메뉴 아이콘 우상단에 떠 있는 배지 (Message management)
export const NotificationDot = styled(Box)({
  ...countBadgeBase,
  position: 'absolute',
  top: '-6px',
  right: '-8px',
});

// 서브메뉴 텍스트 옆 인라인 배지 (Chat)
export const SubMenuNotificationDot = styled('span')({
  ...countBadgeBase,
  marginLeft: '6px',
  verticalAlign: 'middle',
});

// 라이트버전에서 이용 불가(PRO 전용) 메뉴 표시 배지
export const ProBadge = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  marginLeft: '6px',
  padding: '1px 6px',
  borderRadius: '999px',
  fontSize: '9px',
  fontWeight: 700,
  letterSpacing: '0.6px',
  lineHeight: 1.5,
  color: '#58B9C1',
  background: 'rgba(88, 185, 193, 0.12)',
  border: '1px solid rgba(88, 185, 193, 0.35)',
  verticalAlign: 'middle',
  flexShrink: 0,
  userSelect: 'none',
});

// ============================================
// Menu Label
// ============================================
export const MenuLabelPaper = styled(Paper)({
  background: 'transparent',
  boxShadow: 'none',
  marginLeft: '16px',
});

export const MenuLabelText = styled(Typography)({
  marginTop: '3px',
  fontSize: '15px',
  color: 'white',
});

// ============================================
// SubMenu Container
// ============================================
export const SubMenuContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  marginLeft: '60px',
  rowGap: '24px',
});

// ============================================
// SubMenu Item
// ============================================
export const SubMenuItem = styled(Typography)({
  fontSize: '14px',
  '&:hover': {
    color: 'white',
  },
  color: 'grey',
  position: 'relative',
});

// ============================================
// Single Menu Item (no submenus)
// ============================================
export const SingleMenuItem = styled(Box, {
  shouldForwardProp: prop => prop !== 'isExpanded',
})(({ isExpanded }) => ({
  width: '220px',
  height: '60px',
  background: isExpanded ? '#000' : 'rgba(0, 0, 0, .03)',
  textDecoration: 'none',
  padding: '0 12px 0 12px',
  display: 'flex',
  alignItems: 'center',
  '&:hover': {
    cursor: 'pointer',
    background: isExpanded ? '#000' : '#0F7F85',
  },
}));

// ============================================
// Active/Inactive link styles
// ============================================
export const getNavLinkStyle = isActive => ({
  color: isActive ? '#58B9C1' : undefined,
  textDecoration: 'none',
});

export const getIconStyle = isExpanded => ({
  fontSize: '24px',
  color: isExpanded ? '#58B9C1' : '#ededed',
});
